import React, { useEffect, useRef } from "react";
import "../styles/product-landing.css";

const ProductLanding = ({
  title,
  subtitle,
  content,
  href,
  label,
  other,
  src,
  trigger,
  id,
}) => {
  const heroRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current || !imageRef.current) return;

      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;

      const xPos = (clientX / innerWidth - 0.5) * 15;
      const yPos = (clientY / innerHeight - 0.5) * 15;

      imageRef.current.style.transform = `perspective(1000px) rotateY(${xPos}deg) rotateX(${-yPos}deg)`;

      const shapes = heroRef.current.querySelectorAll(".bg-shape");
      shapes.forEach((shape, index) => {
        const speed = (index + 1) * 0.3;
        shape.style.transform = `translate(${xPos * speed}px, ${
          yPos * speed
        }px)`;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section ref={heroRef} className="premium-hero">
      {/* Animated Background */}
      <div className="hero-background">
        <div className="gradient-mesh"></div>
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
        <div className="grid-overlay"></div>

        {/* Floating Particles */}
        <div className="particles">
          {[...Array(15)].map((_, i) => (
            <span
              key={i}
              className="particle"
              style={{
                "--delay": `${Math.random() * 5}s`,
                "--duration": `${15 + Math.random() * 10}s`,
                "--x-end": `${Math.random() * 100}%`,
                "--size": `${3 + Math.random() * 5}px`,
                left: `${Math.random() * 100}%`,
              }}
            ></span>
          ))}
        </div>
      </div>

      <div className="hero-wrapper">
        {/* Left Content */}
        <div className="hero-left">
          {/* Animated Badge */}

          {/* Main Title */}
          <div className="title-container">
            <h1 className="premium-title">
              <span className="title-line">
                <span className="title-word">{title}</span>
              </span>
              <span className="title-line highlight-text">
                <span className="title-word">{subtitle}</span>
              </span>
            </h1>
          </div>

          {/* Description */}
          <div className="premium-description">{content}</div>

          {/* CTA Section */}
          {/* <div className="cta-container">
            <a
              href={href || "https://calendly.com/cloudare"}
              className="premium-btn primary"
            >
              <span className="btn-bg"></span>
              <span className="btn-content">
                <span>{label || "Get Started"}</span>
                <svg className="btn-arrow" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </a>

            <a href="#demo" className="premium-btn secondary">
              <span className="btn-ring"></span>
              <span className="btn-content">
                <span className="play-wrapper">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
                <span>Watch Demo</span>
              </span>
            </a>
          </div> */}
          <div className="premium-badge">
            <div className="badge-glow"></div>
            <div className="badge-content">
              <span className="badge-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="badge-text">Enterprise-Grade Solutions</span>
              {/* <span className="badge-arrow">→</span> */}
            </div>
          </div>

          {/* Stats Row */}
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-value">
                <span className="stat-number">300</span>
                <span className="stat-suffix">+</span>
              </div>
              <span className="stat-label">Integrations</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-value">
                <span className="stat-number">98.9</span>
                <span className="stat-suffix">%</span>
              </div>
              <span className="stat-label">Uptime SLA</span>
            </div>
            <div className="stat-divider"></div>
            {/* <div className="stat-item">
              <div className="stat-value">
                <span className="stat-number">24</span>
                <span className="stat-suffix">/7</span>
              </div>
              <span className="stat-label">Support</span>
            </div> */}
          </div>
        </div>

        {/* Right Content - 3D Image Section */}
        <div className="hero-right">
          <div ref={imageRef} className="image-container">
            {/* Glow Effect */}
            <div className="image-glow"></div>

            {/* Main Image Frame */}
            <div className="image-frame">
              <div className="frame-border"></div>
              <img src={src} alt="ERP Solutions" className="hero-image" />

              {/* Shine Effect */}
              <div className="image-shine"></div>
            </div>

            {/* Floating Feature Cards */}
            <div className="feature-card card-1">
              <div className="card-icon success">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 11.08V12a10 10 0 11-5.93-9.14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M22 4L12 14.01l-3-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="card-info">
                <span className="card-value">Real-time Sync</span>
                <span className="card-label">Instant Updates</span>
              </div>
              <div className="card-pulse"></div>
            </div>

            <div className="feature-card card-2">
              <div className="card-icon primary">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="card-info">
                <span className="card-value">Bank-Level Security</span>
                <span className="card-label">256-bit Encryption</span>
              </div>
              <div className="card-pulse"></div>
            </div>

            <div className="feature-card card-3">
              <div className="card-icon accent">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L2 7l10 5 10-5-10-5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 17l10 5 10-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12l10 5 10-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="card-info">
                <span className="card-value">Multi-Layer API</span>
                <span className="card-label">Seamless Integration</span>
              </div>
              <div className="card-pulse"></div>
            </div>

            {/* Decorative Elements */}
            <div className="deco-circle circle-1"></div>
            <div className="deco-circle circle-2"></div>
            <div className="deco-dots"></div>
          </div>
        </div>
      </div>
      {/* Client Section with Boxed Logos */}
      <div className="client-section">
        <span className="client-label">Trusted by industry leaders</span>
        <div className="all-logo-items">
          <div className="hero-logo-item">Oracle</div>
          <div className="hero-logo-item">Odoo</div>
          <div className="hero-logo-item">Zoho</div>
          <div className="hero-logo-item">MS Dynamics</div>
          <div className="hero-logo-item">Tally</div>
          <div className="hero-logo-item">Sage</div>
          <div className="hero-logo-item">Quickbooks</div>
        </div>
      </div>
    </section>
  );
};

export default ProductLanding;
